import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DiagnoseMultiPicker from './diagnoseMultiPicker';
import { appAlert } from '../../lib/dialogs';
import { filterDiagnoseResults, buildInputDiagnose } from '../../lib/diagnoseMultiPicker';

// TICKET-01712: «Velg flere» is remembered per device.
const MULTI_SELECT_STORAGE_KEY = 'dyrejournal-addDiagnose-multiSelect';

function readMultiSelect() {
    try {
        return util.get(MULTI_SELECT_STORAGE_KEY) === true;
    } catch (err) {
        return false;
    }
}

function isValidDate(dateString) {
    // console.log('isValidDate', dateString);

    const date = new Date(dateString);
    // Check if the date is invalid
    if (isNaN(date)) {
        // console.log('isNaN(date)', isNaN(date));
        return false;
    }
    // Check if the year is before 1800
    if (date.getFullYear() < 1900) {
        // console.log('date.getFullYear() < 1900', date.getFullYear() < 1900);
        return false;
    }
    // If we reach here, the date is valid
    return true;
}

@withText(props => ({
    diagnoseTitle: <Text id='dyrejournal.diagnose-search-title'>Search for diagnose</Text>,
}))
@observer
class AddDiagnose extends Component {
	constructor(props) {
        super(props);
        this.state = {
            inputDiagnose: {},
            addAsDifferential: false,
            multiSelect: readMultiSelect(),
            showPreviousDiagnoses: false,
            previousDiagnosesLoaded: false,
            previousDiagnosesLoading: false,
        };
        this.searchTimer = null;
        this.focusSearch = null;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { diagnoseStore } = this.props.stores;
        await diagnoseStore.load();
    }

    onClickChooseDiagnose = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const { drawerLevel = 1, id: journalId } = this.props;
		const { appState, saleStore, diagnoseStore } = this.props.stores;
        const { newSale } = saleStore;
        const { diagnoses = [] } = newSale;
        const { diagnoses: allDiagnoses } = diagnoseStore;
        const selectedItem = allDiagnoses.find(d => d.id === parseInt(id, 10));
        if (selectedItem) {
            const { journals = [] } = newSale;
            // Add diagnose to journal with id journalId
            const journal = journals.find(j => j.id === parseInt(journalId, 10));
            appState.toggleDrawer(false, drawerLevel);
        }
    }

    // Resolve the journal this drawer is editing. Same lookup order as
    // onClickAddDiagnoseToJournal/render: fallbackJournals, animalJournals
    // (journalStore + saleStore), then the object prop (with or without id).
    getCurrentJournal = () => {
        const { id: jId, object } = this.props;
        const { saleStore, journalStore } = this.props.stores;
        const { animalJournals: saleAnimalJournals } = saleStore;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals } = journalStore;
        const journalId = parseInt(jId || object?.id, 10);
        let journal = null;
        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        if (!journal && object && !object.id) {
            journal = object;
        }
        return journal || {};
    }

    onClickTogglePreviousDiagnoses = async () => {
        const { showPreviousDiagnoses, previousDiagnosesLoaded } = this.state;
        const show = !showPreviousDiagnoses;
        this.setState({ showPreviousDiagnoses: show });
        if (show && !previousDiagnosesLoaded) {
            const { journalStore } = this.props.stores;
            const journal = this.getCurrentJournal();
            this.setState({ previousDiagnosesLoading: true });
            await journalStore.loadPreviousAnimalDiagnoses(journal.animal, journal.id);
            this.setState({ previousDiagnosesLoading: false, previousDiagnosesLoaded: true });
        }
    }

    searchDiagnoseCallback = selectedItem => {
        const { addAsDifferential } = this.state;
        const inputDiagnose = {
            id: selectedItem.id,
            name: selectedItem.name,
            diagnoseCode: selectedItem.diagnoseCode,
            uuidv4: selectedItem.uuidv4,
            speciesAllowed: selectedItem.speciesAllowed,
        };
        if (addAsDifferential) {
            inputDiagnose.differential = 1;
        }
        this.setState({ selectedItem, inputDiagnose }, () => {
            this.onClickAddDiagnoseToJournal();
        });
    }

    onToggleMultiSelect = e => {
        const multiSelect = e.target.checked;
        this.setState({ multiSelect });
        try {
            util.set(MULTI_SELECT_STORAGE_KEY, multiSelect);
        } catch (err) {
            // Storage blocked (private mode): the toggle still works for this drawer.
        }
    }

    onClickAddDiagnoseToJournal = async () => {
        const { inputDiagnose } = this.state;

        // Validate input diagnose
        if (!inputDiagnose || !inputDiagnose.id) {
            (await appAlert('Please search and select a diagnose first'));
            return;
        }

        try {
            const saved = await this.saveDiagnoseToJournal(inputDiagnose);
            if (!saved) {
                (await appAlert('Error: Could not find journal. Please refresh the page and try again.'));
                return;
            }
            // Execute callback and cleanup after successful operation
            await this.finishAddDiagnose();
        } catch (error) {
            console.error('Failed to save diagnose', error);
            (await appAlert('Failed to add diagnose. Please try again.'));
        }
    }

    // «Velg flere» (TICKET-01712): saves one ticked diagnose. Throws so the
    // picker stops the batch and keeps the rest ticked.
    addSelectedDiagnose = async diagnose => {
        const { addAsDifferential } = this.state;
        const saved = await this.saveDiagnoseToJournal(buildInputDiagnose(diagnose, { differential: addAsDifferential }));
        if (!saved) {
            throw new Error('Could not find journal. Please refresh the page and try again.');
        }
    }

    // Stores one diagnose on the journal this drawer edits: PATCH for a saved
    // journal, locally on newJournal for one without an id. Returns false when
    // the journal can't be found.
    saveDiagnoseToJournal = async inputDiagnose => {
        const { saleStore, journalStore } = this.props.stores;
        const { id: jId, object } = this.props;

        const { newSale, animalJournals: saleAnimalJournals } = saleStore;
        const { journals = [] } = newSale;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals, newJournal } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);  // FIX: Use optional chaining

        // FIX: Search in ALL arrays instead of using priority logic
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }

        // If journal not found in arrays, but we have a valid object prop, use it
        if (!journal && object && object.id === journalId) {
            journal = object;
            console.log('Using journal from object prop (not found in stores)', { journal });
        }
        // Also handle objects without ID (new journals)
        if (!journal && object && !object.id) {
            // This is a new journal without an ID - use the object directly
            journal = object;
            console.log('Using newJournal without ID (will save locally)', { journal });
        }

        // FIX: Add proper error handling with user feedback
        if (!journal) {
            console.error('Journal not found and no newJournal object available', {
                journalId,
                jId,
                object,
                fallbackJournals: fallbackJournals?.length,
                journalStoreAnimalJournals: journalStoreAnimalJournals?.length,
                saleAnimalJournals: saleAnimalJournals?.length
            });
            return false;
        }

        // Use journal date or fall back to today
        const journalDate = journal?.date ? new Date(journal.date) : new Date();

        const value = {
            ...inputDiagnose,
            md5: md5(JSON.stringify({ ...inputDiagnose, date: journalDate })).toString(),
            date: journalDate,
        };

        const newDiagnoses = journal.diagnoses ? [...journal.diagnoses] : [];
        newDiagnoses.push(value);

        // OPTION 2: Handle differently based on whether journal has an ID
        if (journal.id) {
            // Journal exists in database - save to backend
            await journalStore.saveField(journal.id, 'diagnoses', value);

            // Update the source array (fallbackJournals, journalStoreAnimalJournals, or saleAnimalJournals)
            if (fallbackJournals && fallbackJournals.length > 0 && fallbackJournals.find(j => j.id === journal.id)) {
                journalStore.updateField(journal.id, 'diagnoses', newDiagnoses);
            }
            if (journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
                const animalJournalsCopy = [...journalStoreAnimalJournals];
                const idx = animalJournalsCopy.findIndex(j => j.id === journal.id);
                if (idx >= 0) {
                    animalJournalsCopy[idx] = { ...animalJournalsCopy[idx], diagnoses: newDiagnoses };
                    journalStore.updateKeyValue('animalJournals', animalJournalsCopy);
                }
            }
            if (saleAnimalJournals && saleAnimalJournals.length > 0) {
                const animalJournalsCopy = [...saleAnimalJournals];
                const idx = animalJournalsCopy.findIndex(j => j.id === journal.id);
                if (idx >= 0) {
                    animalJournalsCopy[idx] = { ...animalJournalsCopy[idx], diagnoses: newDiagnoses };
                    saleStore.updateKeyValue('animalJournals', animalJournalsCopy);
                }
            }

            // Update newJournal if this is the journal being edited
            if (newJournal && newJournal.id === journal.id) {
                journalStore.updateObjectKeyValue('newJournal', 'diagnoses', newDiagnoses);
            }
        } else {
            // OPTION 2: New journal without ID - update locally only
            // Diagnoses will be saved when journal is saved
            console.log('Adding diagnose to newJournal locally (not saved to backend yet)');
            journalStore.updateObjectKeyValue('newJournal', 'diagnoses', newDiagnoses);
        }
        return true;
    }

    // Runs once after a single pick or a whole «Velg flere» batch: reload the
    // visit's journals (saved journal only), run the drawer callback and reset.
    finishAddDiagnose = async () => {
        const { saleLoadJournals = () => {}, callback = () => {} } = this.props;
        if (this.getCurrentJournal().id) {
            await saleLoadJournals();
        }
        // Await callback to ensure any focus changes it makes are complete
        await callback();
        this.setState({ selectedItem: null, inputDiagnose: {}, addAsDifferential: false }, () => {
            // Refocus search input after state update completes
            this.focusSearch?.();
        });
    }

    onClickDeleteDiagnoseFromJournal = async e => {
        const { md5 } = e.target.closest('button').dataset;
        const { saleStore, journalStore } = this.props.stores;
        const { id: jId, saleLoadJournals = () => {}, object, callback = () => {} } = this.props;
        const { newJournal } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);

        try {
            // OPTION 2: Handle differently based on whether journal has an ID
            if (journalId && !isNaN(journalId)) {
                // Journal exists in database - delete from backend
                await journalStore.removeArrayObject({ field: 'diagnoses', id: journalId, md5, objectName: 'newJournal' });
                callback();
                await saleLoadJournals();
            } else if (object && !object.id) {
                // OPTION 2: New journal without ID - update locally only
                const newDiagnoses = (newJournal.diagnoses || []).filter(d => d.md5 !== md5);
                journalStore.updateObjectKeyValue('newJournal', 'diagnoses', newDiagnoses);
                callback();
            } else {
                console.error('Invalid journal ID in onClickDeleteDiagnoseFromJournal', { journalId, jId, object });
                (await appAlert('Error: Invalid journal ID. Please refresh the page.'));
            }
        } catch (error) {
            console.error('Failed to delete diagnose', error);
            (await appAlert('Failed to delete diagnose. Please try again.'));
        }
    }

    onClickEditLine = e => {
        const { md5 } = e.target.closest('tr').dataset;
        const { journalStore, saleStore } = this.props.stores;
        const { id: jId, object } = this.props;  // FIX: Added object from props

        const { animalJournals: saleAnimalJournals } = saleStore;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);  // FIX: Use optional chaining

        // Search in ALL arrays instead of using priority logic
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }

        // If journal not found in arrays, but we have a valid object prop, use it
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        // Also handle objects without ID (new journals)
        if (!journal && object && !object.id) {
            journal = object;
        }

        if (!journal) {
            console.error('Journal not found in onClickEditLine', { journalId, jId, object });
            void appAlert('Error: Could not find journal. Please refresh the page.');
            return;
        }

        const { diagnoses = [] } = journal;  // FIX: Changed "diagnose" to "diagnoses"
        const foundDiagnose = diagnoses.find(m => m.md5 === md5);

        if (!foundDiagnose) {
            console.error('Diagnose not found', { md5, diagnoses });
            void appAlert('Error: Could not find diagnose. Please refresh the page.');
            return;
        }

        const selectedItem = { ...foundDiagnose}
        this.setState({ selectedItem, inputDiagnose: selectedItem });

        journalStore.updateKeyValue('journal', journal);
    }

    onInput = e => {
        const { inputDiagnose } = this.state;
        const { id: journalId } = this.props;
        const { journalStore, saleStore } = this.props.stores;
        const { name, value, type } = e.target;
        const { md5 } = e.target.closest('input').dataset;

        const { journal } = journalStore;

        // FIX: Check if journal and diagnoses exist
        if (!journal || !journal.diagnoses) {
            console.error('Journal or diagnoses not found in onInput', { journal });
            return false;
        }

        const idx = journal.diagnoses.findIndex(m => m.md5 === md5);  // FIX: Changed "diagnose" to "diagnoses"

        // FIX: Validate index
        if (idx < 0) {
            console.error('Diagnose not found in journal', { md5, diagnoses: journal.diagnoses });
            return false;
        }

        inputDiagnose[name] = value;
        this.setState({ inputDiagnose });

        if (type === 'datetime-local' && !isValidDate(value)) {
            return false;
        }
        if (type === 'date' && !isValidDate(value)) {
            return false;
        }

        // updateObjectKeyArrFieldValue(obj, key, idx, field, value) {
        journalStore.updateObjectKeyArrFieldValue('journal', 'diagnoses', idx, name, value);

        clearTimeout(this.updateTimer);
        this.updateTimer = setTimeout(() => {
            this.doUpdate(e);
        }, 1000);
    }

    doUpdate = async (e) => {
        const { id: journalId, object } = this.props;
        const { journalStore } = this.props.stores;
        const { newJournal } = journalStore;
        const { name, value } = e.target;
        const { md5 } = e.target.closest('input').dataset;

        try {
            // OPTION 2: Handle differently based on whether journal has an ID
            if (journalId || (object && object.id)) {
                // Journal exists in database - update backend
                await journalStore.editArrayObject({
                    field: 'diagnoses',
                    key: name,
                    value,
                    id: journalId || object.id,
                    md5,
                });
            } else {
                // OPTION 2: New journal without ID - update locally only
                const diagnoses = newJournal.diagnoses || [];
                const idx = diagnoses.findIndex(d => d.md5 === md5);
                if (idx >= 0) {
                    const updatedDiagnoses = [...diagnoses];
                    updatedDiagnoses[idx] = { ...updatedDiagnoses[idx], [name]: value };
                    journalStore.updateObjectKeyValue('newJournal', 'diagnoses', updatedDiagnoses);
                }
            }
        } catch (error) {
            console.error('Failed to update diagnose field', error);
            (await appAlert('Failed to save changes. Please try again.'));
        }
    }

    onToggleDifferential = async e => {
        const { checked } = e.target;
        const { md5: elementMd5 } = e.target.closest('input').dataset;
        const value = checked ? 1 : 0;
        const { id: journalId, object } = this.props;
        const { journalStore } = this.props.stores;
        const { journal, newJournal } = journalStore;
        const { inputDiagnose } = this.state;

        this.setState({ inputDiagnose: { ...inputDiagnose, differential: value } });

        if (journal && journal.diagnoses) {
            const idx = journal.diagnoses.findIndex(m => m.md5 === elementMd5);
            if (idx >= 0) {
                journalStore.updateObjectKeyArrFieldValue('journal', 'diagnoses', idx, 'differential', value);
            }
        }

        try {
            if (journalId || (object && object.id)) {
                await journalStore.editArrayObject({
                    field: 'diagnoses',
                    key: 'differential',
                    value,
                    id: journalId || object.id,
                    md5: elementMd5,
                });
            } else {
                const diagnoses = newJournal.diagnoses || [];
                const idx = diagnoses.findIndex(d => d.md5 === elementMd5);
                if (idx >= 0) {
                    const updatedDiagnoses = [...diagnoses];
                    updatedDiagnoses[idx] = { ...updatedDiagnoses[idx], differential: value };
                    journalStore.updateObjectKeyValue('newJournal', 'diagnoses', updatedDiagnoses);
                }
            }
        } catch (error) {
            console.error('Failed to update differential flag', error);
            (await appAlert('Failed to save changes. Please try again.'));
        }
    }

    onClickEditDone = async () => {
        const { journalStore } = this.props.stores;
        this.setState({ selectedItem: null, inputDiagnose: {} });
        journalStore.updateKeyValue('journal', {});
    }

    displaySpeciesAllowed(speciesAllowed) {
        if (!speciesAllowed) {
            return '';
        }
        // {"fjorfe":true,"hest":true,"gris":true,"storfe-flokk":true,"storfe-hann":true,"storfe-hunn":true,"smaafe":true}
        const species = Object.keys(speciesAllowed);
        return species.filter(o => speciesAllowed[o]).join(', ');
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const {
            selectedItem,
            inputDiagnose,
            multiSelect,
            showPreviousDiagnoses,
            previousDiagnosesLoaded,
            previousDiagnosesLoading,
        } = this.state;
        const {
            diagnoseTitle,
            id: jId,
            object,
        } = this.props;
        const { userStore, diagnoseStore, saleStore, journalStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { newSale, animalJournals: saleAnimalJournals } = saleStore;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals, newJournal, previousAnimalDiagnoses } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);

        // Search in ALL arrays for existing journal
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }

        // If journal not found in arrays, but we have a valid object prop, use it
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        // Also handle objects without ID (new journals)
        if (!journal && object && !object.id) {
            journal = object;
        }

        // Default to empty object if still not found
        if (!journal) {
            journal = {};
        }

        const { diagnoses = [] } = journal;

        // Previously used diagnoses for this animal, minus the ones already on
        // the current journal. filter() returns a copy — the store observable is
        // never mutated.
        const previousDiagnosesToShow = previousAnimalDiagnoses.filter(pd => !diagnoses.some(d => (
            pd.diagnoseCode ? d.diagnoseCode === pd.diagnoseCode : d.id === pd.id
        )));

        const addOptions = (
            <div class='d-flex flex-row flex-wrap column-gap-4 my-2'>
                <div class='form-check'>
                    <input
                        class='form-check-input'
                        type='checkbox'
                        id='addDiagnoseMultiSelect'
                        checked={multiSelect}
                        onInput={this.onToggleMultiSelect}
                    />
                    <label class='form-check-label' for='addDiagnoseMultiSelect'>
                        <Text id='dyrejournal.diagnose-multi-toggle'>Select several</Text>
                    </label>
                </div>
                <div class='form-check'>
                    <input
                        class='form-check-input'
                        type='checkbox'
                        id='addAsDifferential'
                        checked={this.state.addAsDifferential}
                        onInput={e => this.setState({ addAsDifferential: e.target.checked })}
                    />
                    <label class='form-check-label' for='addAsDifferential'>
                        <Text id='dyrejournal.diagnose-add-as-differential'>Add as differential diagnosis</Text>
                    </label>
                </div>
            </div>
        );

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='padding-top: 20px; padding-bottom: 100px;'>

                    <div class='w-100 d-flex flex-sm-row flex-column'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.add-diagnose'>Add diagnose</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                {multiSelect ? (
                                    <DiagnoseMultiPicker
                                        setFocusCallback={fn => this.focusSearch = fn}
                                        diagnoseStore={diagnoseStore}
                                        journalDiagnoses={diagnoses}
                                        saveOne={this.addSelectedDiagnose}
                                        onDone={this.finishAddDiagnose}
                                    >
                                        {addOptions}
                                    </DiagnoseMultiPicker>
                                ) : <>
                                    <DyrejournalSaleSearch
                                        setFocusCallback={fn => this.focusSearch = fn}
                                        stores={this.props.stores}
                                        key={'diagnoses'}
                                        keyName={'diagnoses'}
                                        title={diagnoseTitle}
                                        holdingStore={diagnoseStore}
                                        searchResultList={'searchResults'}
                                        searchResultsLine={(e, idx, selectedIndex) => {
                                            return(<>
                                                <div class={`d-flex flex-row justify-content-between px-3 py-1 ${idx > 0 ? 'border-top' : ''} ${idx === selectedIndex ? 'bg-secondary' : ''}`}>
                                                    <div>
                                                        {e.diagnoseCode}:  {e.name} <span class='font-weight-lighter'>({this.displaySpeciesAllowed(e.speciesAllowed)})</span>
                                                    </div>
                                                </div>
                                            </>);
                                        }}
                                        callback={this.searchDiagnoseCallback}
                                        focusOnMount={true}
                                        // 3 or fewer digits filter by diagnoseCode prefix, otherwise the wider search
                                        filterResults={filterDiagnoseResults}
                                    />
                                    {addOptions}
                                </>}
                                {!!journal.animal && <>
                                    <div class='mb-2'>
                                        <button
                                            type='button'
                                            class='btn btn-outline-secondary btn-sm rounded-pill'
                                            onClick={this.onClickTogglePreviousDiagnoses}
                                        >
                                            <i class='fa-duotone fa-clock-rotate-left me-1' />
                                            <Text id='dyrejournal.diagnose-previous'>Previous diagnoses</Text>
                                            {previousDiagnosesLoaded && previousDiagnosesToShow.length > 0 && <span class='badge text-bg-secondary ms-1'>{previousDiagnosesToShow.length}</span>}
                                            <i class={`fa-solid ${showPreviousDiagnoses ? 'fa-chevron-up' : 'fa-chevron-down'} ms-1`} />
                                        </button>
                                    </div>
                                    {showPreviousDiagnoses && <div class='mb-2'>
                                        {previousDiagnosesLoading ? <>
                                            <div class='text-body-secondary py-1'>
                                                <i class='fa-duotone fa-spinner fa-spin me-1' />
                                                <Text id='dyrejournal.diagnose-previous-loading'>Loading previous diagnoses...</Text>
                                            </div>
                                        </> : <>
                                            {previousDiagnosesToShow.length > 0 ? <>
                                                <div class='table-responsive'>
                                                    <table class='table table-sm table-striped mb-0'>
                                                        <thead>
                                                            <tr>
                                                                <th><Text id='dyrejournal.diagnose-code'>Diagnose Code</Text></th>
                                                                <th><Text id='dyrejournal.diagnose-name'>Name</Text></th>
                                                                <th><Text id='dyrejournal.diagnose-previous-last-used'>Last used</Text></th>
                                                                <th>&nbsp;</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {previousDiagnosesToShow.map(e => {
                                                                return(
                                                                    <tr key={e.diagnoseCode || e.id}>
                                                                        <td>{e.diagnoseCode}</td>
                                                                        <td>{e.name}</td>
                                                                        <td>{e.lastUsedDate ? util.formatDate(e.lastUsedDate, { locale: 'nb-NO' }).split(',')[0] : ''}</td>
                                                                        <td>
                                                                            <button
                                                                                type='button'
                                                                                class='btn btn-primary btn-sm py-1 rounded-pill'
                                                                                onClick={() => this.searchDiagnoseCallback(e)}
                                                                            >
                                                                                <nobr>
                                                                                    <i class='fa-solid fa-plus me-1' />
                                                                                    <Text id='input.add'>Add</Text>
                                                                                </nobr>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </> : <>
                                                <div class='text-body-secondary py-1'>
                                                    <Text id='dyrejournal.diagnose-previous-none'>No previous diagnoses found for this animal</Text>
                                                </div>
                                            </>}
                                        </>}
                                    </div>}
                                </>}
                                <div class={`table-responsive ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                    <table class='table table-striped table-sm mb-0'>
                                        <thead>
                                            <tr>
                                                <th>&nbsp;</th>
                                                <th><Text id='dyrejournal.diagnose-code'>Diagnose Code</Text></th>
                                                <th><Text id='dyrejournal.diagnose-name'>Name</Text></th>
                                                <th><Text id='dyrejournal.diagnose-species-allowed'>Species allowed</Text></th>
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {diagnoses && diagnoses.length > 0 && diagnoses.map((e, idx) => {
                                                return(<>
                                                    <tr data-md5={e.md5}>
                                                        <td onClick={this.onClickEditLine}>{e.md5 === inputDiagnose.md5 ? <i class='fa-duotone fa-circle fa-bounce' /> : <i class='fa-duotone fa-pen' />}</td>
                                                        <td>{e.diagnoseCode}</td>
                                                        <td>
                                                            {e.name}
                                                            {!!e.differential && <span class='badge text-bg-warning ms-1' title='Differensialdiagnose'><Text id='dyrejournal.diagnose-differential-short'>DD</Text></span>}
                                                        </td>
                                                        <td>{this.displaySpeciesAllowed(e.speciesAllowed)}</td>
                                                        <td>
                                                            <button
                                                                class='btn btn-danger btn-sm py-1 rounded-pill'
                                                                onClick={this.onClickDeleteDiagnoseFromJournal}
                                                                data-md5={e.md5}
                                                            >
                                                                <nobr>
                                                                    <i class='fa-solid fa-trash me-1' />
                                                                    <Text id='input.delete'>Delete</Text>
                                                                </nobr>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                </>);
                                            })}

                                            {selectedItem && selectedItem.id && <>
                                                {inputDiagnose.md5 ? <>
                                                    <tr class='bg-secondary'>
                                                        <th colspan='11'><Text id='dyrejournal.diagnose-edit'>Editing diagnose</Text></th>
                                                    </tr>
                                                    <tr class='bg-secondary'>
                                                        <th>&nbsp;</th>
                                                        <td><small>{inputDiagnose.diagnoseCode}</small></td>
                                                        <td>
                                                            <small>{inputDiagnose.name}</small>
                                                            <div class='form-check'>
                                                                <input
                                                                    class='form-check-input'
                                                                    type='checkbox'
                                                                    id='editDifferential'
                                                                    data-md5={inputDiagnose.md5}
                                                                    checked={!!inputDiagnose.differential}
                                                                    onInput={this.onToggleDifferential}
                                                                />
                                                                <label class='form-check-label' for='editDifferential'>
                                                                    <Text id='dyrejournal.diagnose-differential'>Differential diagnosis</Text>
                                                                </label>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <button class='btn btn-primary btn-sm py-1 rounded-pill' onClick={this.onClickEditDone}>
                                                                <nobr>
                                                                    <i class='fa-solid fa-floppy-disk me-1' />
                                                                    <Text id='input.done'>Done</Text>
                                                                </nobr>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                </> : <>
                                                    <tr>
                                                        <th>&nbsp;</th>
                                                        <td><small>{inputDiagnose.diagnoseCode}</small></td>
                                                        <td><small>{inputDiagnose.name}</small></td>
                                                        <td>
                                                            <nobr>
                                                                <button class='btn btn-primary btn-sm py-1 rounded-pill' onClick={this.onClickAddDiagnoseToJournal}>
                                                                    <i class='fa-solid fa-plus me-1' />
                                                                    <Text id='input.add'>Add</Text>
                                                                </button>
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                </>}
                                            </>}
                                        </tbody>
                                    </table>
                                </div>
                                {/* <xmp>{JSON.stringify(selectedItem, null, 2)}</xmp> */}

                            </div>
                        </div>
                        {/* <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.journals'>Journals</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                Report for today's journals
                            </div>
                        </div> */}
                    </div>

                </div>
            </div>
        );
    }
}

export default AddDiagnose;
